import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useApiClient } from "../api/useApiClient";
import { ApiError } from "../api/client";
import { useRoles } from "../auth/useRoles";
import { ROLE_APROBADOR_PAGOS } from "../constants/roles";
import { AvisoError } from "../components/AvisoError";
import { DetalleLotePago } from "../components/DetalleLotePago";
import { ModalMotivo } from "../components/ModalMotivo";
import type { EstadoDetalleLote } from "../components/LotesPagoPanel";
import type { LotePago, LotePagoDetalle } from "../types/reviewItem";
import { formatMonto } from "../utils/formato";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import { motivoRangoInvalido } from "../utils/pagosBanco";
import {
  APROBACIONES_NECESARIAS,
  CLASE_ESTADO_LOTE,
  ETIQUETA_ESTADO_LOTE,
  EVENTO_LOTES_PAGO_CAMBIARON,
  TABS_APROBACION_PAGOS,
  aprobacionOptimista,
  aprobacionesVigentes,
  decisionAprobacion,
  estadosDeTab,
  lotesDeTab,
  mensajeErrorDecision,
  mensajeErrorLote,
  nombreCorto,
  sinLote,
  textoAprobaciones,
  textoConfirmarAprobar,
  textoResultadoAprobacion,
  type TabAprobacionPagos,
} from "../utils/lotesPago";

const COLUMNAS = 6;

const SIN_LOTES: Record<TabAprobacionPagos, string> = {
  para_aprobar: "No hay lotes para aprobar. Aparecen acá cuando Contaduría vincula un lote con el PDF del banco y cierra.",
  aprobados: "No hay lotes aprobados esperando la firma en el banco.",
  para_corregir: "No hay lotes rechazados esperando corrección.",
  pagados: "No hay lotes pagados en este período.",
};

function estadoError(err: unknown): { status: number | null; texto: string } {
  return { status: err instanceof ApiError ? err.status : null, texto: err instanceof Error ? err.message : "" };
}

/**
 * Página "Aprobación de pagos". [NUEVO 04/11/2026, Tarea 10]
 *
 * La usan los Aprobadores de pagos justo antes de firmar en el banco.
 * Contaduría la ve en modo consulta (sin Aprobar/Rechazar). Detrás del flag
 * `lotes_pago`: apagado, la página no pide nada más que /funcionalidades.
 *
 * Aprobar y rechazar son optimistas: el lote sale de "Para aprobar" en el
 * momento (o se queda con "1 de 2" si era la primera aprobación) y, si la
 * llamada falla, vuelve a su lugar. Haya funcionado o no, después se recarga
 * la lista y el detalle desde el backend (mismo criterio que useAccion).
 */
export function AprobacionPagosPage() {
  const api = useApiClient();
  const roles = useRoles();
  const { accounts } = useMsal();
  const usuario = accounts[0]?.username ?? "";
  const esAprobador = roles.includes(ROLE_APROBADOR_PAGOS);

  const [funcionalidad, setFuncionalidad] = useState<"cargando" | "si" | "no" | "error">("cargando");
  const [tab, setTab] = useState<TabAprobacionPagos>("para_aprobar");
  const tabRef = useRef<TabAprobacionPagos>("para_aprobar");
  const [lotes, setLotes] = useState<LotePago[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [detalle, setDetalle] = useState<EstadoDetalleLote | null>(null);
  const detalleId = useRef<number | null>(null);
  const [comentario, setComentario] = useState("");
  const [rechazando, setRechazando] = useState<LotePagoDetalle | null>(null);
  // Solo se aplica la respuesta del último pedido: una lenta no pisa a una nueva.
  const pedido = useRef(0);

  // Filtro de fechas de "Pagados" (mismo criterio que el de lotes del banco).
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const filtroFechas = useRef<{ desde?: string; hasta?: string } | null>(null);
  const [filtroActivo, setFiltroActivo] = useState(false);
  const [errorRango, setErrorRango] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    api
      .obtenerFuncionalidades()
      .then((f) => {
        if (!cancelado) setFuncionalidad(f.lotes_pago ? "si" : "no");
      })
      .catch(() => {
        if (!cancelado) setFuncionalidad("error");
      });
    return () => {
      cancelado = true;
    };
  }, [api]);

  /** `silencioso`: recarga después de una acción, sin la pantalla de "Cargando...". */
  const cargarLotes = useCallback(
    async (silencioso = false) => {
      const n = ++pedido.current;
      const t = tabRef.current;
      if (!silencioso) setCargando(true);
      try {
        const fechas = t === "pagados" ? filtroFechas.current ?? {} : {};
        const resultado = await api.listarLotesPago({ estado: estadosDeTab(t), ...fechas });
        if (n !== pedido.current) return;
        setLotes(resultado);
        setErrorCarga(null);
      } catch (err) {
        if (n !== pedido.current) return;
        const { status, texto } = estadoError(err);
        if (silencioso) {
          setError((prev) => prev ?? "No se pudo actualizar la lista. Recargá la página.");
        } else {
          setErrorCarga(mensajeErrorLote(status, texto, "cargar los lotes"));
        }
      } finally {
        if (n === pedido.current) setCargando(false);
      }
    },
    [api]
  );

  const cargarDetalle = useCallback(
    async (id: number) => {
      try {
        const lote = await api.obtenerLotePago(id);
        if (detalleId.current === id) setDetalle({ id, lote, error: null });
      } catch (err) {
        if (detalleId.current !== id) return;
        const { status, texto } = estadoError(err);
        setDetalle({ id, lote: null, error: mensajeErrorLote(status, texto, "cargar el detalle del lote") });
      }
    },
    [api]
  );

  useEffect(() => {
    if (funcionalidad === "si") cargarLotes();
  }, [funcionalidad, tab, cargarLotes]);

  function cerrarDetalle() {
    detalleId.current = null;
    setDetalle(null);
    setComentario("");
  }

  function abrirDetalle(id: number) {
    detalleId.current = id;
    setDetalle({ id, lote: null, error: null });
    setComentario("");
    cargarDetalle(id);
  }

  function alternarDetalle(id: number) {
    if (detalleId.current === id) cerrarDetalle();
    else abrirDetalle(id);
  }

  function cambiarTab(t: TabAprobacionPagos) {
    if (t === tabRef.current) return;
    tabRef.current = t;
    cerrarDetalle();
    setLotes([]);
    setError(null);
    setCargando(true); // sin esto, el mensaje de "no hay lotes" parpadea antes de cargar
    setTab(t);
  }

  async function aplicarFiltroFechas() {
    const motivo = motivoRangoInvalido(desde, hasta);
    setErrorRango(motivo);
    if (motivo) return;
    filtroFechas.current = { desde: desde || undefined, hasta: hasta || undefined };
    setFiltroActivo(true);
    cerrarDetalle();
    await cargarLotes();
  }

  async function quitarFiltroFechas() {
    filtroFechas.current = null;
    setFiltroActivo(false);
    setErrorRango(null);
    setDesde("");
    setHasta("");
    cerrarDetalle();
    await cargarLotes();
  }

  /** Después de aprobar o rechazar, haya funcionado o no: el estado real del backend. */
  async function recargarDespuesDeAccion() {
    const recargas: Array<Promise<unknown>> = [cargarLotes(true)];
    if (detalleId.current !== null) recargas.push(cargarDetalle(detalleId.current));
    await Promise.all(recargas);
    // El badge del menú cuenta los lotes "Para aprobar".
    window.dispatchEvent(new Event(EVENTO_LOTES_PAGO_CAMBIARON));
  }

  async function aprobar(lote: LotePagoDetalle) {
    if (!window.confirm(textoConfirmarAprobar(lote))) return;
    const anterior = lotes;
    const enLista = lotes.find((l) => l.id === lote.id);
    // Con la aprobación que completa las necesarias el lote sale de la lista.
    const sale = (enLista?.aprobaciones.length ?? 0) + 1 >= APROBACIONES_NECESARIAS;
    setError(null);
    setAviso(null);
    setOcupado(true);
    setLotes((prev) => aprobacionOptimista(prev, lote.id, usuario, new Date().toISOString()));
    if (sale) cerrarDetalle();
    try {
      const r = await api.aprobarLotePago(lote.id, lote.version, comentario.trim() || undefined);
      setAviso(textoResultadoAprobacion(lote.nombre, r));
      setComentario("");
    } catch (err) {
      setLotes(anterior);
      const { status, texto } = estadoError(err);
      setError(mensajeErrorDecision(status, texto, "aprobar el lote"));
      if (sale) abrirDetalle(lote.id);
    } finally {
      await recargarDespuesDeAccion();
      setOcupado(false);
    }
  }

  async function rechazar(lote: LotePagoDetalle, motivo: string) {
    setRechazando(null);
    const anterior = lotes;
    setError(null);
    setAviso(null);
    setOcupado(true);
    setLotes((prev) => sinLote(prev, lote.id));
    cerrarDetalle();
    try {
      await api.rechazarLotePago(lote.id, lote.version, motivo);
      setAviso(`${nombreCorto(lote.nombre)} quedó rechazado: vuelve a Contaduría para corregirlo.`);
    } catch (err) {
      setLotes(anterior);
      const { status, texto } = estadoError(err);
      setError(mensajeErrorDecision(status, texto, "rechazar el lote"));
      abrirDetalle(lote.id);
    } finally {
      await recargarDespuesDeAccion();
      setOcupado(false);
    }
  }

  async function abrirPdfBanco(loteBancoId: number) {
    try {
      abrirEnNuevaPestana(await api.obtenerPdfLoteBanco(loteBancoId));
    } catch (err) {
      const { status, texto } = estadoError(err);
      setError(mensajeErrorLote(status, texto, "abrir el PDF del banco"));
    }
  }

  function accionesDelDetalle(lote: LotePagoDetalle) {
    const d = decisionAprobacion(lote, aprobacionesVigentes(lote), usuario, esAprobador);
    const conPdf = lote.lote_banco_id !== null && lote.lote_banco_id !== undefined;
    if (!conPdf && !d.puedeAprobar && !d.puedeRechazar && !d.motivoNoAprobar) return undefined;
    return (
      <>
        {conPdf ? (
          <button className="btn-secundario" onClick={() => abrirPdfBanco(lote.lote_banco_id as number)}>
            PDF del banco
          </button>
        ) : null}
        {d.puedeAprobar ? (
          <>
            <input
              type="text"
              aria-label="Comentario de la aprobación (opcional)"
              placeholder="Comentario (opcional)"
              maxLength={1000}
              value={comentario}
              disabled={ocupado}
              onChange={(e) => setComentario(e.target.value)}
            />
            <button disabled={ocupado} onClick={() => aprobar(lote)}>
              Aprobar
            </button>
          </>
        ) : null}
        {d.motivoNoAprobar ? <span className="sin-resolver">{d.motivoNoAprobar}</span> : null}
        {d.puedeRechazar ? (
          <button className="btn-danger" disabled={ocupado} onClick={() => setRechazando(lote)}>
            Rechazar
          </button>
        ) : null}
      </>
    );
  }

  if (funcionalidad === "cargando") return <p>Cargando...</p>;
  if (funcionalidad !== "si") {
    return (
      <div>
        <h1>Aprobación de pagos</h1>
        <p className="subtitle">
          {funcionalidad === "no"
            ? "Esta página no está disponible por ahora."
            : "No se pudo abrir la página. Recargá en unos segundos."}
        </p>
      </div>
    );
  }

  const tabActual = TABS_APROBACION_PAGOS.find((t) => t.clave === tab) ?? TABS_APROBACION_PAGOS[0];
  const visibles = lotesDeTab(tab, lotes);

  return (
    <div>
      <h1>Aprobación de pagos</h1>
      <p className="subtitle">
        Revisá cada lote antes de firmarlo en el banco. Hacen falta {APROBACIONES_NECESARIAS} aprobaciones de personas
        distintas; quien armó el lote no lo aprueba.
        {esAprobador ? "" : " Estás en modo consulta: aprobar y rechazar es solo para Aprobadores de pagos."}
      </p>

      <AvisoError mensaje={error} onCerrar={() => setError(null)} />
      {aviso ? (
        <div className="aviso-info">
          {aviso}
          <button className="btn-ghost" onClick={() => setAviso(null)}>
            Cerrar
          </button>
        </div>
      ) : null}

      <div className="tabla-toolbar">
        <div className="filtro-tabs" role="group" aria-label="Lotes por estado">
          {TABS_APROBACION_PAGOS.map(({ clave, etiqueta }) => (
            <button
              key={clave}
              type="button"
              aria-pressed={tab === clave}
              className={`filtro-tab${tab === clave ? " filtro-tab-activo" : ""}`}
              onClick={() => cambiarTab(clave)}
            >
              {etiqueta}
              {tab === clave && !cargando && !errorCarga ? (
                <>
                  {" "}
                  <span className="filtro-tab-cantidad">{visibles.length}</span>
                </>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {tabActual.conFechas ? (
        <div className="filtro-lotes-banco" role="group" aria-label="Filtrar lotes pagados por fecha">
          <label>
            Desde <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </label>
          <label>
            Hasta <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </label>
          <button className="btn-secundario" disabled={cargando} onClick={aplicarFiltroFechas}>
            Buscar
          </button>
          {filtroActivo ? (
            <button className="btn-ghost" disabled={cargando} onClick={quitarFiltroFechas}>
              Ver los más recientes
            </button>
          ) : null}
          {errorRango ? <span className="error-text">{errorRango}</span> : null}
          <span className="sin-resolver">
            {filtroActivo
              ? "Lotes armados en el rango elegido."
              : "Se muestran los más recientes. Filtrá por fecha para ver anteriores."}
          </span>
        </div>
      ) : null}

      {cargando ? <p className="subtitle">Cargando lotes...</p> : null}
      {!cargando && errorCarga ? (
        <p className="error-text">
          {errorCarga}{" "}
          <button className="btn-ghost" onClick={() => cargarLotes()}>
            Reintentar
          </button>
        </p>
      ) : null}
      {!cargando && !errorCarga && visibles.length === 0 ? <p className="sin-resolver">{SIN_LOTES[tab]}</p> : null}

      {!cargando && !errorCarga && visibles.length > 0 ? (
        <table className="compact lotes-pago">
          <thead>
            <tr>
              <th>Lote</th>
              <th className="celda-monto">Total</th>
              <th className="celda-monto">Facturas / proveedores</th>
              <th>Estado</th>
              <th>Aprobaciones</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((l) => {
              const abierto = detalle !== null && detalle.id === l.id;
              return (
                <Fragment key={l.id}>
                  <tr>
                    <td className="wrap-ok">
                      <b>{l.nombre}</b>
                      <span className="sub-dato">Armó {l.creado_por.split("@")[0]}</span>
                    </td>
                    <td className="mono celda-monto">
                      {formatMonto(l.total)}
                      <span className="moneda">{l.moneda}</span>
                    </td>
                    <td className="mono celda-monto">
                      {l.cantidad_facturas} / {l.cantidad_proveedores}
                    </td>
                    <td className="wrap-ok">
                      <span className={`badge ${CLASE_ESTADO_LOTE[l.estado]}`}>{ETIQUETA_ESTADO_LOTE[l.estado]}</span>
                      {l.motivo_rechazo ? (
                        <span className="sub-dato rejection-reason" title={l.motivo_rechazo}>
                          Motivo: {l.motivo_rechazo}
                        </span>
                      ) : null}
                    </td>
                    <td className="wrap-ok">{textoAprobaciones(l.aprobaciones)}</td>
                    <td className="actions-cell">
                      <button className="btn-ghost" aria-expanded={abierto} onClick={() => alternarDetalle(l.id)}>
                        {abierto ? "Ocultar detalle" : tab === "para_aprobar" && esAprobador ? "Revisar" : "Ver detalle"}
                      </button>
                    </td>
                  </tr>
                  {abierto && detalle ? (
                    <tr className="detalle-row">
                      <td colSpan={COLUMNAS}>
                        {detalle.error ? <p className="error-text">{detalle.error}</p> : null}
                        {!detalle.error && detalle.lote === null ? <p className="subtitle">Cargando detalle...</p> : null}
                        {detalle.lote ? (
                          <DetalleLotePago lote={detalle.lote} acciones={accionesDelDetalle(detalle.lote)} />
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      ) : null}

      <ModalMotivo
        abierto={rechazando !== null}
        titulo="Rechazar lote de pago"
        detalle={
          rechazando
            ? `${nombreCorto(rechazando.nombre)} · ${rechazando.moneda} ${formatMonto(rechazando.total)} · ` +
              `${rechazando.cantidad_proveedores} proveedor(es). Vuelve a Contaduría para corregirlo y se ` +
              "desvincula del PDF del banco."
            : undefined
        }
        onConfirmar={(motivo) => {
          if (rechazando) rechazar(rechazando, motivo);
        }}
        onCancelar={() => setRechazando(null)}
      />
    </div>
  );
}
